import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import type { StandardEquipmentItem } from "@slfinrtl/ui-bff-routes"
import type { PropsWithChildren } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { ImagesWithDescription } from "src/components"

const messages = defineMessages({
	imageAlt: {
		defaultMessage: "Image {number} of option: {name}",
		id: "standardEquipment.categories.richItem.image.alt",
	},
	mismatchDisclaimer: {
		defaultMessage:
			"These images are only visualizing this option and do not reflect the actual configuration of the vehicle.",
		id: "standardEquipment.categories.richItem.mismatchDisclaimer",
	},
	optionCodePrefix: {
		defaultMessage: "Optioncode {code}",
		id: "standardEquipment.categories.richItem.optionCodePrefix",
	},
	sampleImageDisclaimer: {
		defaultMessage:
			"This photo material only exemplifies this configuration option and does not reflect the vehicle. " +
			"You can see the actual colors, options and materials on the photos of the vehicle.",
		id: "standardEquipment.categories.richItem.sampleImageBadgeDisclaimer",
	},
	sampleImageNoteLabel: {
		defaultMessage: "About this photo material",
		id: "standardEquipment.categories.richItem.sampleImageBadgeLabel",
	},
	thumbnailAlt: {
		defaultMessage: "Select image {number} of option: {name}",
		id: "standardEquipment.categories.richItem.thumbnail.alt",
	},
})

interface RichEquipmentCategoryItemDetailsProps {
	item: StandardEquipmentItem
}

export const RichEquipmentCategoryItemDetails = ({ item }: RichEquipmentCategoryItemDetailsProps) => {
	const { formatMessage } = useIntl()

	const images = item.imageSourceSets?.map((image, index) => ({
		alt: formatMessage(messages.imageAlt, {
			name: item.name,
			number: index + 1,
		}),
		imageSourceSet: image,
		thumbnailAlt: formatMessage(messages.thumbnailAlt, {
			name: item.name,
			number: index + 1,
		}),
	}))

	return (
		<ImagesWithDescription
			images={images}
			title={item.name}
			description={item.description}
			additionalContent={
				item.optionCode && (
					<OptionCode>
						<FormattedMessage {...messages.optionCodePrefix} values={{ code: item.optionCode }} />
					</OptionCode>
				)
			}
			footer={{
				text: formatMessage(messages.sampleImageDisclaimer),
				title: formatMessage(messages.sampleImageNoteLabel),
			}}
		/>
	)
}

const OptionCode = styled(({ children }: PropsWithChildren) => (
	<PText color="contrast-medium" size="xs" weight="normal">
		{children}
	</PText>
))`
	text-transform: uppercase;
`
